import { useState, useCallback, useRef, useEffect } from 'react';
import { FileDocumentType } from '@nkr/enums/document-type';
import { ILineItem } from '../types/line-item.types';

interface UseReceiptFilesProps {
    fetchFileUrl: (docType: FileDocumentType, fileId: number) => Promise<string | null>;
}

export const useReceiptFiles = (props: UseReceiptFilesProps) => {
    const [receiptFileUrls, setReceiptFileUrls] = useState<Map<number, string>>(new Map());
    const receiptFileUrlsRef = useRef<Map<number, string>>(new Map());
    
    useEffect(() => {
        receiptFileUrlsRef.current = receiptFileUrls;
    }, [receiptFileUrls]);

    const aggregateReceiptFiles = useCallback(async (
        lineItems: ILineItem[],
        tripId?: number,
        tripFileIds?: number[],
        loadFirstOnly: boolean = false
    ): Promise<Map<number, string>> => {
        const fileIds = lineItems
            .filter(li => li.receiptFileId)
            .map(li => li.receiptFileId!);
        
        if (fileIds.length === 0) return new Map();

        const uniqueFileIds = [...new Set(fileIds)];
        const filesToFetch = loadFirstOnly && uniqueFileIds.length > 0 
            ? [uniqueFileIds[0]]
            : uniqueFileIds;

        const fileUrls = new Map<number, string>();
        const filesToDownload: number[] = [];
        
        for (const fileId of filesToFetch) {
            if (receiptFileUrlsRef.current.has(fileId)) {
                const cachedUrl = receiptFileUrlsRef.current.get(fileId);
                if (cachedUrl) {
                    fileUrls.set(fileId, cachedUrl);
                }
            } else {
                filesToDownload.push(fileId);
            }
        }
        
        for (const fileId of filesToDownload) {
            try {
                const url = await props.fetchFileUrl(FileDocumentType.Receipts, fileId);
                if (url) {
                    fileUrls.set(fileId, url);
                }
            } catch (err) {
            }
        }
        
        if (fileUrls.size > 0) {
            if (tripId && tripFileIds) {
                const filteredUrls = new Map<number, string>();
                fileUrls.forEach((url, fileId) => {
                    if (tripFileIds.includes(fileId)) {
                        filteredUrls.set(fileId, url);
                    }
                });
                setReceiptFileUrls(prev => {
                    const newMap = new Map(prev);
                    filteredUrls.forEach((url, fileId) => {
                        newMap.set(fileId, url);
                    });
                    return newMap;
                });
                return filteredUrls;
            } else {
                setReceiptFileUrls(prev => {
                    const newMap = new Map(prev);
                    fileUrls.forEach((url, fileId) => {
                        newMap.set(fileId, url);
                    });
                    return newMap;
                });
                return fileUrls;
            }
        }
        
        return new Map();
    }, [props]);

    const fetchFileUrl = useCallback(async (fileId: number): Promise<string | null> => {
        try {
            const url = await props.fetchFileUrl(FileDocumentType.Receipts, fileId);
            if (url) {
                setReceiptFileUrls(prev => {
                    const newMap = new Map(prev);
                    newMap.set(fileId, url);
                    return newMap;
                });
                return url;
            }
            return null;
        } catch (err) {
            return null;
        }
    }, [props]);

    return {
        receiptFileUrls,
        aggregateReceiptFiles,
        fetchFileUrl
    };
};

